// The Running Order result screen (CLAUDE.md: Result screen order).
//
// In order, with one gap between every section and none tighter than another:
// the result line, the answer, "Guesses", Share, then the aggregate panels, then
// the next game, then the countdown as a plain status line.
//
// The aggregate panels need results posted to a server and a minimum number of
// players on the puzzle; neither exists yet, so this screen is the state
// CLAUDE.md calls the hidden version — the outcome, the reveal and the actions.
// Preview mode (development only, src/dev/preview.ts) fills them with invented
// numbers, because a panel that cannot be shown cannot be checked either.
import { useState } from 'react';
import { GameHeader } from '../../components/GameHeader.tsx';
import { Distribution, MISSED, guessLabel, type DistRow } from '../../components/Distribution.tsx';
import { PREVIEW, CROWD } from '../../dev/preview.ts';
import { distributionRows, pct, playersLabel, usePuzzleStats, type RunningOrderTitle } from '../../lib/puzzle-stats.ts';
import { useArrival, useNextPuzzle } from '../../lib/next-puzzle.ts';
import { NextButton, NextCountdown } from '../../components/NextButton.tsx';
import { copyToClipboard, shareText } from './share.ts';
import { posterUrl, releasedLabel, MAX_GUESSES, type AnswerFilm, type GuessMarks } from './types.ts';
import '../../components/result.css';
import './Result.css';

interface Props {
  number: number;
  /** The correct order with years, from the server, sent only once the game is over. */
  answer: AnswerFilm[];
  won: boolean;
  guesses: GuessMarks[];
  /** The arrangements submitted, which the server checks before it says how
   *  everybody did. Empty in preview mode, which invents the figures. */
  submitted: number[][];
  /** A replay from the archive: no Share, because a replay is not a result. */
  replay?: boolean;
}

export function Result({ number, answer, won, guesses, submitted, replay = false }: Props) {
  const [shareLabel, setShareLabel] = useState('Share');
  // Each game's own next puzzle for this device, never this one, or its
  // countdown once every released one is finished (lib/next-puzzle.ts).
  const nextSheet = useNextPuzzle('call-sheet');
  const nextDaily = useNextPuzzle('running-order', number);
  // When the next daily actually appears, for the countdown at the foot.
  const dailyArrives = useArrival('running-order');

  async function share() {
    const ok = await copyToClipboard(shareText(number, won, guesses));
    setShareLabel(ok ? 'Copied' : "Couldn't copy");
    setTimeout(() => setShareLabel('Share'), 2000);
  }


  // Both panels are read from the same records, so their figures agree. Below a
  // minimum number of players on the puzzle they are both hidden and the screen
  // is the outcome, the reveal and the actions alone — which is every puzzle,
  // until results are being recorded.
  // Behind the flag, so that when the site is built this reads as `false && …`
  // and the invented numbers are dropped from the bundle with it.
  const own = won ? guessLabel(guesses.length) : MISSED;
  const stats = usePuzzleStats<RunningOrderTitle>('running-order', number, submitted, !PREVIEW);
  const real = !PREVIEW && stats?.shown ? stats : null;
  const rows: DistRow[] = PREVIEW
    ? [
      ...CROWD.guesses.map((pct, i) => ({ label: guessLabel(i + 1), value: `${pct}%`, pct })),
      { label: MISSED, value: `${CROWD.missed}%`, pct: CROWD.missed }
    ].map((row) => ({ ...row, own: row.label === own }))
    : real ? distributionRows(real.distribution ?? [], real.players, own) : [];
  const players = PREVIEW ? CROWD.players : real?.players ?? 0;
  // Misplaced, as a share of the players, by film; the films in the right order.
  const misplaced = (id: number, i: number) => PREVIEW
    ? CROWD.misplaced[i] ?? 0
    : pct(real?.titles?.find((t) => t.id === id)?.misplaced ?? 0, players);

  return (
    <main className="column result ro-result">
      <GameHeader game="Running Order" number={number} />

      {/* The line and the answer, grouped so the line sits exactly where the
          instruction sat while the game was being played. No panel — see the
          game screen. */}
      <section className="ro-reveal">
        {/* The panel opens on one line, and which one is the whole difference
            between winning and losing.
            A win gets its score — "Correct in 2 of 3", written nowhere else on
            the screen. A loss gets no score and no headline naming the loss: the
            guess rows below already show three attempts with nothing filled. It
            gets "The correct order:" instead, which heads the posters and is the
            only thing a player who has run out is there for. The two lines are
            the same height, so the panel is. */}

        {won
          ? <p className="panel-line">Correct in {guesses.length} of {MAX_GUESSES}</p>
          : <h2 className="panel-line">The correct order:</h2>}

        {/* The answer. Five posters above the switch point, a row each below it.
            Call Sheet's line is "The correct answer:" — the same pattern, and the
            only difference is that an order is not an answer. */}
        <div className="ro-answer">
          {answer.map((film) => (
            <figure className="ro-answer-film" key={film.id}>
              {posterUrl(film.poster)
                ? <img className="ro-answer-poster" src={posterUrl(film.poster)!} alt="" />
                : <span className="ro-answer-poster" />}
              <figcaption>
                <span className="ro-answer-title">{film.title}</span>
                <span className="ro-answer-year">{releasedLabel(film)}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* The guess number, then one flat bar per slot — filled where that card
          was in place on that guess. No "X of 5 correct" label: the bars say it. */}
      <section className="ro-guesses">
        <h2 className="heading">Guesses</h2>
        {guesses.map((row, i) => (
          <div className="ro-guess-row" key={i}>
            <span className="ro-guess-no" aria-hidden="true">{i + 1}</span>
            <span className="ro-marks" aria-hidden="true">
              {row.map((right, slot) => (
                <span className={`ro-mark${right ? ' is-right' : ''}`} key={slot} />
              ))}
            </span>
            <span className="visually-hidden">
              Guess {i + 1}: {row.filter(Boolean).length} of {row.length} in place
            </span>
          </div>
        ))}
      </section>

      {!replay && <button type="button" className="btn btn-primary btn-block" data-share onClick={share}>{shareLabel}</button>}

      {/* Below the player threshold the panels are hidden and nothing takes
          their place (owner, 12 October). */}
      {(PREVIEW || real) && (
        <section className="panel">
          <div className="panel-head">
            <h2 className="heading">Guess distribution</h2>
            <span className="panel-count">{playersLabel(players)}</span>
          </div>
          <Distribution rows={rows} />
        </section>
      )}

      {/* The mirror of Call Sheet's "Top titles for guess 1": how often each film
          was put in the wrong place, on the first guess. No years — they are
          already under the posters. */}
      {(PREVIEW || real) && (
        <section className="panel">
          <h2 className="heading">Title statistics</h2>
          <p className="panel-sub">Misplaced by players</p>
          <div className="title-stats">
            {answer.map((film, i) => (
              <div className="title-stat" key={film.id}>
                <span className="title-stat-name">{film.title}</span>
                <span className="title-stat-pct">{misplaced(film.id, i)}%</span>
                <span className="title-stat-bar" style={{ width: `${misplaced(film.id, i)}%` }} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The other game, then a daily the player has missed — never a filler
          button: on launch day there is no missed daily, so there is one button. */}
      <div className="next-row">
        <NextButton game="call-sheet" next={nextSheet} />
        <NextButton game="running-order" next={nextDaily} />
      </div>

      {/* The time carries the accent, as in the design — everything around it is
          the muted status-line grey. No date: the daily is numbered, not dated,
          everywhere a player sees it. */}
      {/* Not twice: where every daily is finished, the countdown already
          stands in the Running Order button's place above. */}
      {nextDaily?.pick !== 'done' && <NextCountdown game="running-order" at={dailyArrives} className="ro-countdown" />}
    </main>
  );
}
